import React, {
  MutableRefObject, ReactNode, WheelEvent, useEffect, useLayoutEffect, useRef, useState,
} from "react"
import { Box, Flex } from "@chakra-ui/react"
import SimpleBar from "simplebar-react"
import { PreviewDevice } from "components/elements/DeviceToggle"
import Phone, {
  PHONE_FRAME_INSET,
  PHONE_FULL_HEIGHT,
  PHONE_HOME_INDICATOR_HEIGHT,
  PHONE_SCREEN_CORNER_RADIUS,
  PHONE_SCREEN_INSET,
  PHONE_SCREEN_WIDTH,
  PHONE_STATUS_BAR_HEIGHT,
} from "components/Phone/Phone"
import StatusBar from "components/Phone/StatusBar"
import { isDarkColor } from "utilities/colors"
import TopBar, {
  BROWSER_CHROME_HEIGHT as CHROME_HEIGHT,
  BROWSER_CORNER_RADIUS as CORNER_RADIUS,
} from "./PreviewFrameWithBranding/TopBar"
import { SECTION_EDIT_WIDTH, SECTION_LABEL_WIDTH } from "./PreviewBlock"

const MAX_WINDOW_WIDTH = 1000
const FULL_WIDTH_TOOLBAR_BELOW = 720
const TOOLBAR_LABELS_MIN_WIDTH = 820
const MARGIN = 24
const GUTTERS = SECTION_LABEL_WIDTH + SECTION_EDIT_WIDTH
const SCROLLBAR_INSET = 2

// Gutters plus the screen with rounded bottom corners, so content never shows past them
const screenClipPath = (screenWidth: number, layerHeight: number, radius: number) => {
  const height = Math.max(0, layerHeight)
  const r = Math.min(radius, height)
  const left = SECTION_LABEL_WIDTH
  const right = SECTION_LABEL_WIDTH + screenWidth

  return `path("M0 0 H${right + SECTION_EDIT_WIDTH} V${height} H${right} V${height - r} `
    + `A${r} ${r} 0 0 1 ${right - r} ${height} H${left + r} A${r} ${r} 0 0 1 ${left} ${height - r} `
    + `V${height} H0 Z")`
}

const FADE_HEIGHT = 24

type ScrollEdges = { atTop: boolean, atBottom: boolean }

const gutterFade = ({ atTop, atBottom }: ScrollEdges) => `linear-gradient(to bottom, ${
  atTop ? "black" : `transparent, black ${FADE_HEIGHT}px`}, ${
  atBottom ? "black" : `black calc(100% - ${FADE_HEIGHT}px), transparent`})`

// Fades labels and Edit buttons at the scroll edges; the screen column stays opaque
const gutterFadeMask = (screenWidth: number, edges: ScrollEdges) => ({
  maskImage: `${gutterFade(edges)}, linear-gradient(black, black), ${gutterFade(edges)}`,
  maskSize: `${SECTION_LABEL_WIDTH}px 100%, ${screenWidth}px 100%, ${SECTION_EDIT_WIDTH}px 100%`,
  maskPosition: `0 0, ${SECTION_LABEL_WIDTH}px 0, ${SECTION_LABEL_WIDTH + screenWidth}px 0`,
  maskRepeat: "no-repeat",
})

type ScrollLayerProps = {
  scrollRef: MutableRefObject<HTMLElement | undefined>
  scrollEdges: ScrollEdges
  darkBackground: boolean
  screenWidth: number
  clipPath?: string
  contentPaddingBottom?: string
  children: ReactNode
} & Record<"top" | "bottom" | "left" | "width", string>

// One scroll layer over the screen and gutters, so labels and Edit buttons follow their sections
const ScrollLayer = ({
  scrollRef, scrollEdges, darkBackground, top, bottom, left, width, screenWidth, clipPath,
  contentPaddingBottom, children,
}: ScrollLayerProps) => (
  <Box
    position="absolute"
    top={top}
    bottom={bottom}
    left={left}
    w={width}
    sx={{
      clipPath,
      ...gutterFadeMask(screenWidth, scrollEdges),
      "& .simplebar-scrollbar:before": darkBackground ? { bg: "white" } : undefined,
      "& .simplebar-track.simplebar-vertical": {
        right: `${SECTION_EDIT_WIDTH + SCROLLBAR_INSET}px`,
        bottom: `${SCROLLBAR_INSET}px`,
      },
    }}
  >
    <SimpleBar style={{ height: "100%" }} autoHide={false} scrollableNodeProps={{ ref: scrollRef }}>
      <Box pb={contentPaddingBottom}>{children}</Box>
    </SimpleBar>
  </Box>
)

type Props = {
  device: PreviewDevice
  toolbar: (compact: boolean) => ReactNode
  topBackground: string
  background: string
  children: ReactNode
}

// Shows the portal at real size in a browser window that fills the pane, or in a phone.
const PreviewStage = ({
  device, toolbar, topBackground, background, children,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const toolbarRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLElement>()
  const [scrollEdges, setScrollEdges] = useState<ScrollEdges>({ atTop: true, atBottom: true })
  const [size, setSize] = useState({ width: 0, height: 0, toolbarHeight: 0 })
  const hasSize = size.width > 0

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) {
      return undefined
    }
    const updateSize = () => setSize({
      width: container.clientWidth,
      height: container.clientHeight,
      toolbarHeight: toolbarRef.current?.offsetHeight ?? 0,
    })
    updateSize()
    const observer = new ResizeObserver(updateSize)
    observer.observe(container)
    if (toolbarRef.current) {
      observer.observe(toolbarRef.current)
    }

    return () => observer.disconnect()
  }, [])

  // Wheel anywhere in the pane scrolls the preview, not only over it.
  const scrollPreviewFromPane = (event: WheelEvent<HTMLDivElement>) => {
    const scrollable = scrollRef.current
    const target = event.target as Node
    if (scrollable && event.currentTarget.contains(target) && !scrollable.contains(target)) {
      scrollable.scrollBy({ top: event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY })
    }
  }
  const darkBackground = isDarkColor(background)

  // Fades only show toward content that is scrolled out of view.
  useEffect(() => {
    const scrollable = scrollRef.current
    if (!scrollable) {
      return undefined
    }
    const updateEdges = () => {
      const atTop = scrollable.scrollTop <= 0
      const atBottom = scrollable.scrollTop + scrollable.clientHeight >= scrollable.scrollHeight - 1
      setScrollEdges((edges) => (
        edges.atTop === atTop && edges.atBottom === atBottom ? edges : { atTop, atBottom }
      ))
    }
    updateEdges()
    scrollable.addEventListener("scroll", updateEdges, { passive: true })
    const observer = new ResizeObserver(updateEdges)
    observer.observe(scrollable)
    if (scrollable.firstElementChild) {
      observer.observe(scrollable.firstElementChild)
    }

    return () => {
      scrollable.removeEventListener("scroll", updateEdges)
      observer.disconnect()
    }
  }, [hasSize, device])

  const windowWidth = Math.max(0, Math.min(MAX_WINDOW_WIDTH, size.width - GUTTERS - 2 * MARGIN))
  const stageHeight = Math.max(0, size.height - size.toolbarHeight - 3 * MARGIN)
  const phoneHeight = Math.min(PHONE_FULL_HEIGHT, stageHeight)
  const phoneIsFullHeight = phoneHeight >= PHONE_FULL_HEIGHT
  const phoneScreenBottom = phoneIsFullHeight ? PHONE_SCREEN_INSET : 0
  const phoneStageWidth = Math.max(windowWidth, PHONE_SCREEN_WIDTH)
  const phoneScreenLeft = SECTION_LABEL_WIDTH + (phoneStageWidth - PHONE_SCREEN_WIDTH) / 2
  // Narrow panes give the toolbar the gutters' width too; labels hide only when still too narrow
  const fullWidthToolbar = windowWidth < FULL_WIDTH_TOOLBAR_BELOW
  const toolbarWidth = fullWidthToolbar ? GUTTERS + windowWidth : windowWidth

  return (
    <Flex
      ref={containerRef}
      direction="column"
      align="center"
      h={1}
      flex={1}
      w="full"
      overflow="hidden"
      onWheel={scrollPreviewFromPane}
    >
      <Box
        w={`${GUTTERS + (device === "mobile" ? phoneStageWidth : windowWidth)}px`}
        pt={`${MARGIN}px`}
      >
        <Box
          ref={toolbarRef}
          ml={fullWidthToolbar ? 0 : `${SECTION_LABEL_WIDTH}px`}
          w={`${toolbarWidth}px`}
          mb={`${MARGIN}px`}
        >
          {toolbar(toolbarWidth < TOOLBAR_LABELS_MIN_WIDTH)}
        </Box>
        {hasSize && (device === "desktop" ? (
          <Box position="relative" h={`${stageHeight}px`}>
            <Box
              position="absolute"
              top={0}
              left={`${SECTION_LABEL_WIDTH}px`}
              w={`${windowWidth}px`}
              h="full"
              bg={background}
              borderRadius={`${CORNER_RADIUS}px`}
              boxShadow="lg"
            >
              <TopBar variant="lg" />
            </Box>
            <ScrollLayer
              scrollRef={scrollRef}
              scrollEdges={scrollEdges}
              darkBackground={darkBackground}
              top={`${CHROME_HEIGHT}px`}
              bottom="0px"
              left="0px"
              width="100%"
              screenWidth={windowWidth}
              clipPath={screenClipPath(windowWidth, stageHeight - CHROME_HEIGHT, CORNER_RADIUS)}
            >
              {children}
            </ScrollLayer>
            <Box
              position="absolute"
              top={0}
              left={`${SECTION_LABEL_WIDTH}px`}
              w={`${windowWidth}px`}
              h="full"
              borderWidth={1}
              borderColor="gray.300"
              borderRadius={`${CORNER_RADIUS}px`}
              pointerEvents="none"
            />
          </Box>
        ) : (
          <Box position="relative" h={`${phoneHeight}px`}>
            <Box
              position="absolute"
              top={0}
              left={`${phoneScreenLeft - PHONE_SCREEN_INSET}px`}
              filter="drop-shadow(0 10px 15px rgba(0, 0, 0, 0.12))"
            >
              <Phone height={phoneHeight}>
                <Flex direction="column" h="full">
                  <Box h={`${PHONE_STATUS_BAR_HEIGHT - PHONE_FRAME_INSET}px`} pt={6} bg={topBackground}>
                    <StatusBar light={isDarkColor(topBackground)} />
                  </Box>
                  <Box flex={1} bg={background} />
                </Flex>
              </Phone>
            </Box>
            <ScrollLayer
              scrollRef={scrollRef}
              scrollEdges={scrollEdges}
              darkBackground={darkBackground}
              top={`${PHONE_STATUS_BAR_HEIGHT}px`}
              bottom={`${phoneScreenBottom}px`}
              left={`${phoneScreenLeft - SECTION_LABEL_WIDTH}px`}
              width={`${GUTTERS + PHONE_SCREEN_WIDTH}px`}
              screenWidth={PHONE_SCREEN_WIDTH}
              contentPaddingBottom={phoneIsFullHeight ? `${PHONE_HOME_INDICATOR_HEIGHT}px` : undefined}
              clipPath={phoneIsFullHeight ? screenClipPath(
                PHONE_SCREEN_WIDTH,
                phoneHeight - PHONE_STATUS_BAR_HEIGHT - phoneScreenBottom,
                PHONE_SCREEN_CORNER_RADIUS,
              ) : undefined}
            >
              {children}
            </ScrollLayer>
          </Box>
        ))}
      </Box>
    </Flex>
  )
}

export default PreviewStage
